import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Td,
  Tr,
  Box,
  Text,
  Checkbox,
  Menu,
  MenuButton,
  MenuList,
} from '@chakra-ui/react';
import { flexRender, Row } from '@tanstack/react-table';
import React from 'react';

import {
  CalcTermsCategory,
  CalcTermsDynamicFormula,
  CalcTermsRow,
  formatCellValue,
  isCalcTermsDynamicFormulaArray,
  termKeyColumnId,
} from './calculationTerminologiesUtils';
import { colors } from '~/packages/common-frontend/colors';
import { FormulaContainer } from '~/packages/common-frontend/components/FormulaContainer';
import { SearchBarInput } from '~/packages/common-frontend/components/SearchAndCopyComponents';

const TableTd = ({ children }: { children: React.ReactNode }) => {
  return (
    <Td cursor="default" fontSize="sm" padding="16px" verticalAlign="top">
      {children}
    </Td>
  );
};

type TableFormulaArrayContentProps = { formulas: unknown[] };
const TableFormulaArrayContent = ({
  formulas,
}: TableFormulaArrayContentProps) => {
  if (!isCalcTermsDynamicFormulaArray(formulas)) {
    return (
      <Td cursor="default" fontSize="sm" padding="16px">
        {formulas.toString()}
      </Td>
    );
  }

  // Bucket formulas by credit facility
  const formulasByCreditFacility = formulas.reduce(
    (acc, formula) => ({
      ...acc,
      [formula.creditFacilityId]: {
        creditFacilityId: formula.creditFacilityId,
        creditFacilityName: formula.creditFacilityName,
        creditFacilityFormulas: [
          ...(acc[formula.creditFacilityId]?.creditFacilityFormulas || []),
          formula,
        ],
      },
    }),
    {} as Record<
      string,
      {
        creditFacilityId: string;
        creditFacilityName: string;
        creditFacilityFormulas: CalcTermsDynamicFormula[];
      }
    >,
  );

  return (
    <>
      {Object.values(formulasByCreditFacility).map(
        ({ creditFacilityId, creditFacilityName, creditFacilityFormulas }) => {
          if (
            creditFacilityFormulas.length < 1 ||
            creditFacilityFormulas.every(({ formula }) => formula === null)
          ) {
            return null;
          }

          return (
            <Box key={creditFacilityId} mb="16px">
              <Text fontWeight={500} mb="8px">
                {creditFacilityName}:
              </Text>
              {creditFacilityFormulas.length > 1 &&
                creditFacilityFormulas.map(
                  ({ loanProductId, loanProductName, formula }) => {
                    if (!formula) {
                      return null;
                    }

                    return (
                      <Box key={loanProductId} mb="16px">
                        <Text>{loanProductName}:</Text>
                        <FormulaContainer>{formula}</FormulaContainer>
                      </Box>
                    );
                  },
                )}

              {creditFacilityFormulas.length === 1 && (
                <FormulaContainer>{formulas[0].formula}</FormulaContainer>
              )}
            </Box>
          );
        },
      )}
    </>
  );
};

type FormulaContentProps = { cellValue: unknown };
export const FormulaContent = ({ cellValue }: FormulaContentProps) => {
  if (!cellValue) {
    return null;
  }
  if (Array.isArray(cellValue)) {
    return <TableFormulaArrayContent formulas={cellValue} />;
  }
  if (typeof cellValue === 'string') {
    return <FormulaContainer>{cellValue}</FormulaContainer>;
  }
  return null;
};

type CalculationTerminologiesTableRowProps = { row: Row<CalcTermsRow> };
export const CalculationTerminologiesTableRow = ({
  row,
}: CalculationTerminologiesTableRowProps) => {
  return (
    <Tr key={row.id}>
      {row.getVisibleCells().map((cell) => {
        const cellValue = cell.getValue();
        const columnId = cell.column.id;

        const formattedCellValue = formatCellValue({
          columnId,
          value: cellValue,
        });

        return (
          <TableTd key={cell.id}>
            {columnId === termKeyColumnId && (
              <Text fontWeight={500}>
                {flexRender(formattedCellValue, cell.getContext())}
              </Text>
            )}
            {columnId === 'definition' && (
              <Text color={colors.black[70]}>
                {flexRender(formattedCellValue, cell.getContext())}
              </Text>
            )}
            {columnId === 'formula' && <FormulaContent cellValue={cellValue} />}
          </TableTd>
        );
      })}
    </Tr>
  );
};

type CalculationTerminologiesSearchBarProps = {
  globalFilter: string;
  setGlobalFilter: React.Dispatch<React.SetStateAction<string>>;
};
export const CalculationTerminologiesSearchBar = ({
  globalFilter,
  setGlobalFilter,
}: CalculationTerminologiesSearchBarProps) => {
  return (
    <Box
      borderRadius="8px"
      padding="16px"
      width="100%"
      backgroundColor={colors.background[100]}
    >
      <SearchBarInput
        query={globalFilter}
        onChangeQuery={setGlobalFilter}
        inputProps={{ placeholder: 'Enter words to search' }}
      />
    </Box>
  );
};

const onToggleTermsCategoryFilter = ({
  termsCategory,
  selectedTermsCategories,
  setSelectedTermsCategories,
}: {
  termsCategory: CalcTermsCategory;
  selectedTermsCategories: Set<CalcTermsCategory>;
  setSelectedTermsCategories: React.Dispatch<
    React.SetStateAction<Set<CalcTermsCategory>>
  >;
}) => {
  const isCategoryCurrentlySelected = selectedTermsCategories.has(
    termsCategory,
  );

  const newCategories = new Set(selectedTermsCategories);

  switch (termsCategory) {
    case 'borrowingBaseCash':
      if (!isCategoryCurrentlySelected) {
        newCategories.add(termsCategory);
        if (selectedTermsCategories.has('borrowingBaseReceivables')) {
          newCategories.add('borrowingBaseReport');
        }
        setSelectedTermsCategories(newCategories);
      } else {
        newCategories.delete(termsCategory);
        newCategories.delete('borrowingBaseReport');
        setSelectedTermsCategories(newCategories);
      }
      break;

    case 'borrowingBaseReceivables':
      if (!isCategoryCurrentlySelected) {
        newCategories.add(termsCategory);
        if (selectedTermsCategories.has('borrowingBaseCash')) {
          newCategories.add('borrowingBaseReport');
        }
        setSelectedTermsCategories(newCategories);
      } else {
        newCategories.delete(termsCategory);
        newCategories.delete('borrowingBaseReport');
        setSelectedTermsCategories(newCategories);
      }
      break;

    case 'borrowingBaseReport':
      if (!isCategoryCurrentlySelected) {
        newCategories.add(termsCategory);
        newCategories.add('borrowingBaseCash');
        newCategories.add('borrowingBaseReceivables');
        setSelectedTermsCategories(newCategories);
      } else {
        newCategories.delete(termsCategory);
        newCategories.delete('borrowingBaseCash');
        newCategories.delete('borrowingBaseReceivables');
        setSelectedTermsCategories(newCategories);
      }
      break;

    case 'metrics':
      if (!isCategoryCurrentlySelected) {
        newCategories.add(termsCategory);

        setSelectedTermsCategories(newCategories);
      } else {
        newCategories.delete(termsCategory);
        setSelectedTermsCategories(newCategories);
      }
      break;
    default:
      setSelectedTermsCategories(selectedTermsCategories);
  }
};

type CalculationTerminologiesMenuFilterProps = {
  selectedTermsCategories: Set<CalcTermsCategory>;
  setSelectedTermsCategories: React.Dispatch<
    React.SetStateAction<Set<CalcTermsCategory>>
  >;
};
export const CalculationTerminologiesMenuFilter = ({
  selectedTermsCategories,
  setSelectedTermsCategories,
}: CalculationTerminologiesMenuFilterProps) => {
  return (
    <Menu
      matchWidth
      autoSelect={false}
      eventListeners={{ scroll: false, resize: true }}
    >
      <MenuButton>
        <ChevronDownIcon />
      </MenuButton>
      <MenuList
        zIndex="dropdown"
        display="grid"
        padding="24px"
        background={colors.background[50]}
        borderRadius="4px"
        border="none"
        box-shadow="0px 1px 2px 0px rgba(0, 0, 0, 0.30)"
      >
        <Text fontWeight={500} fontSize="16px" mb="16px">
          Terms
        </Text>
        <Checkbox
          isChecked={
            selectedTermsCategories.has('borrowingBaseCash') &&
            selectedTermsCategories.has('borrowingBaseReceivables')
          }
          onChange={() =>
            onToggleTermsCategoryFilter({
              setSelectedTermsCategories,
              selectedTermsCategories,
              termsCategory: 'borrowingBaseReport',
            })
          }
          padding="8px"
          fontSize="16px"
          fontWeight={400}
        >
          Borrowing Base Report
        </Checkbox>
        <Checkbox
          isChecked={selectedTermsCategories.has('borrowingBaseCash')}
          onChange={() =>
            onToggleTermsCategoryFilter({
              setSelectedTermsCategories,
              selectedTermsCategories,
              termsCategory: 'borrowingBaseCash',
            })
          }
          ml="32px"
          padding="8px"
          fontSize="16px"
          fontWeight={400}
        >
          Borrowing Base Cash
        </Checkbox>
        <Checkbox
          isChecked={selectedTermsCategories.has('borrowingBaseReceivables')}
          onChange={() =>
            onToggleTermsCategoryFilter({
              setSelectedTermsCategories,
              selectedTermsCategories,
              termsCategory: 'borrowingBaseReceivables',
            })
          }
          ml="32px"
          padding="8px"
          fontSize="16px"
          fontWeight={400}
        >
          Borrowing Base Receivables
        </Checkbox>
        <Checkbox
          isChecked={selectedTermsCategories.has('metrics')}
          onChange={() =>
            onToggleTermsCategoryFilter({
              setSelectedTermsCategories,
              selectedTermsCategories,
              termsCategory: 'metrics',
            })
          }
          padding="8px"
          fontSize="16px"
          fontWeight={400}
        >
          Metrics
        </Checkbox>
        <Checkbox
          isChecked={selectedTermsCategories.has('configuration')}
          onChange={() =>
            onToggleTermsCategoryFilter({
              setSelectedTermsCategories,
              selectedTermsCategories,
              termsCategory: 'configuration',
            })
          }
          padding="8px"
          fontSize="16px"
          fontWeight={400}
        >
          Configuration
        </Checkbox>
      </MenuList>
    </Menu>
  );
};
